import { useParams, useNavigate } from 'react-router-dom';
import { Stack, Title, Tabs, Group, Button, Paper, Text, Loader, Center, Badge, Divider, Timeline } from '@mantine/core';
import { IconEdit, IconArrowLeft, IconPhone, IconMail, IconVideo, IconMapPin, IconCalendar, IconNote } from '@tabler/icons-react';
import { useCompany } from '@/hooks/useCompanies';
import { ShopList } from './ShopList';
import { OwnerList } from './OwnerList';
import type { CompanyStatus, DemoActivity } from '@/types/company';

const STATUS_LABELS: Record<CompanyStatus, string> = {
  DEMO_REQUESTED: 'Neu',
  DEMO_CONTACTED: 'Kontaktiert',
  DEMO_SCHEDULED: 'Termin',
  DEMO_COMPLETED: 'Abgeschlossen',
  DEMO_DECLINED: 'Abgelehnt',
  pending: 'Registriert',
  active: 'Aktiv',
  inactive: 'Inaktiv',
};

const STATUS_COLORS: Record<CompanyStatus, string> = {
  DEMO_REQUESTED: 'orange',
  DEMO_CONTACTED: 'blue',
  DEMO_SCHEDULED: 'cyan',
  DEMO_COMPLETED: 'teal',
  DEMO_DECLINED: 'red',
  pending: 'indigo',
  active: 'green',
  inactive: 'gray',
};

const CONTACT_PREFERENCE_LABELS: Record<string, string> = {
  phone: 'Telefonisch',
  email: 'Per E-Mail',
  video: 'Videocall',
  inperson: 'Persönlich vor Ort',
};

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: company, isLoading } = useCompany(id ?? '');

  if (isLoading) {
    return <Center h={200}><Loader /></Center>;
  }

  if (!company) {
    return <Text>Unternehmen nicht gefunden.</Text>;
  }

  const addr = company.billingAddress;
  const addressDisplay = typeof addr === 'string'
    ? addr
    : [
        [addr.street, addr.houseNumber].filter(Boolean).join(' '),
        [addr.postalCode, addr.city].filter(Boolean).join(' '),
        addr.country,
      ].filter(Boolean).join(', ');

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group>
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/companies')}>
            Zurück
          </Button>
          <Title order={2}>{company.name}</Title>
          {company.legalForm && <Text c="dimmed">({company.legalForm})</Text>}
          <Badge color={STATUS_COLORS[company.status] ?? 'gray'} variant="light" size="lg">
            {STATUS_LABELS[company.status] ?? company.status}
          </Badge>
        </Group>
        <Button leftSection={<IconEdit size={16} />} onClick={() => navigate(`/companies/${id}/edit`)}>
          Bearbeiten
        </Button>
      </Group>

      <Tabs defaultValue="details">
        <Tabs.List>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          {company.status.startsWith('DEMO_') && (
            <Tabs.Tab value="activities">Aktivitäten ({company.demoActivities?.length ?? 0})</Tabs.Tab>
          )}
          <Tabs.Tab value="shops">Shops ({company.shops?.length ?? 0})</Tabs.Tab>
          <Tabs.Tab value="owners">Inhaber ({company.owners?.length ?? 0})</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details" pt="md">
          <Paper withBorder p="md" radius="md">
            <Stack gap="xs">
              <Group>
                <Text fw={600} w={160}>E-Mail:</Text>
                <Text>{company.email}</Text>
              </Group>
              <Group>
                <Text fw={600} w={160}>Telefon:</Text>
                <Text>{company.phone || '-'}</Text>
              </Group>
              <Group>
                <Text fw={600} w={160}>Website:</Text>
                <Text>{company.website || '-'}</Text>
              </Group>
              <Group>
                <Text fw={600} w={160}>Adresse:</Text>
                <Text>{addressDisplay || '-'}</Text>
              </Group>

              {company.status.startsWith('DEMO_') && (
                <>
                  <Divider my="sm" label="Demo-Anfrage Details" labelPosition="left" />
                  <Group>
                    <Text fw={600} w={160}>Ansprechpartner:</Text>
                    <Text>{[company.contactFirstName, company.contactLastName].filter(Boolean).join(' ') || '-'}</Text>
                  </Group>
                  <Group>
                    <Text fw={600} w={160}>Quelle:</Text>
                    <Text>{company.source === 'restaurant' ? 'Restaurant-Seite' : 'Händler-Seite'}</Text>
                  </Group>
                  {company.industry && (
                    <Group>
                      <Text fw={600} w={160}>Branche:</Text>
                      <Text>{company.industry}</Text>
                    </Group>
                  )}
                  {company.contactPreference && (
                    <Group>
                      <Text fw={600} w={160}>Kontaktart:</Text>
                      <Text>{CONTACT_PREFERENCE_LABELS[company.contactPreference] ?? company.contactPreference}</Text>
                    </Group>
                  )}
                  {company.demoMessage && (
                    <Group align="flex-start">
                      <Text fw={600} w={160}>Nachricht:</Text>
                      <Text style={{ flex: 1 }}>{company.demoMessage}</Text>
                    </Group>
                  )}
                </>
              )}
            </Stack>
          </Paper>
        </Tabs.Panel>

        {company.status.startsWith('DEMO_') && (
          <Tabs.Panel value="activities" pt="md">
            <Paper withBorder p="md" radius="md">
              {company.demoActivities && company.demoActivities.length > 0 ? (
                <Timeline active={company.demoActivities.length - 1} bulletSize={24} lineWidth={2}>
                  {[...company.demoActivities].reverse().map((activity: DemoActivity) => (
                    <Timeline.Item
                      key={activity.id}
                      bullet={<IconNote size={12} />}
                      title={activity.action}
                    >
                      <Text size="xs" c="dimmed">
                        {new Date(activity.timestamp).toLocaleString('de-DE')}
                      </Text>
                      {activity.note && (
                        <Text size="sm" mt="xs" style={{ whiteSpace: 'pre-wrap' }}>
                          {activity.note}
                        </Text>
                      )}
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <Text c="dimmed" ta="center">Noch keine Aktivitäten</Text>
              )}
            </Paper>
          </Tabs.Panel>
        )}

        <Tabs.Panel value="shops" pt="md">
          <ShopList companyId={company.id} shops={company.shops} />
        </Tabs.Panel>

        <Tabs.Panel value="owners" pt="md">
          <OwnerList companyId={company.id} owners={company.owners} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
